import { DataSource } from '@angular/cdk/collections';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { signal } from '@angular/core';
import { Column } from '@qtek/shared/models';
import {
  BehaviorSubject,
  iif,
  isObservable,
  map,
  mergeMap,
  Observable,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import {
  MetadataHistoryComponent,
  PsGroupByComponent,
  QtFilterComponent,
} from './components';
import { QtFilterDirective, QtSearchDirective } from './directives';
import { ExtendDataTable, QtGroupBy, SearchPredicate } from './models';
import {
  defaultSearchPredicate,
  filter,
  isExtendedDataTable,
  MetaControls,
  paginate,
  qGroupBy,
  qtGroupByMeta,
  QtRouterSerializer,
  search,
  sort,
  ViewControl,
} from './utils';
import { valueAccessor } from './value-accessor';

/**
 * Produce data stream from provided data and connected controls.
 *
 * ```typescript
 * const initialData: User[] = [];
 *
 * // You can pass {@link QtRouterSerializer} to read and write params from url.
 * const dataSouce = new QtDataSource<User>(initialData);
 *
 * // Set components that control flow of data.
 * dataSource.setControls({
 *   paginator,
 * });
 *
 * // Connect to get lastest data.
 * dataSource.connect().subscribe(console.log);
 *
 * // Disconnect to prevent memory leaks.
 * dataSource.disconnect();
 * ```
 */
export class QtDataSource<T = any> extends DataSource<T> {
  /** Instance of MatPaginator */
  paginator: MatPaginator | null = null;

  /** Instance of MatSort */
  sort: MatSort | null = null;

  /** Instance of {@link QtSearchDirective} to link search to data. */
  search: QtSearchDirective | null = null;

  /** Instance of {@link QtFilterDirective} to link filters to data. */
  filter: QtFilterDirective | null = null;

  /** Instance of {@link QtFilterComponent} to link filters to data. */
  filterComp: QtFilterComponent | null = null;

  /** Instance of {@link PsGroupByComponent} to link filters to data. */
  groupByComponent: PsGroupByComponent | null = null;

  /** Instance of {@link ViewControl} to change view of the data. */
  viewControl: ViewControl | null = null;

  /** Instance of {@link MetaControls} to have server sync. */
  metaControls: MetaControls | null = null;

  /** Instance of {@link MetadataHistoryComponent} to link history to data. */
  historyControl: MetadataHistoryComponent | null = null;

  set filterComponent(fc: QtFilterComponent) {
    if (!fc) return;

    const oldFilterValue = this.filterComp?.filterOptionsState$.getValue();

    this.filterComp = fc;
    const { filter } = fc;
    this.filter = filter;

    if (oldFilterValue) {
      this.filterComp.filterOptionsState$.next(oldFilterValue);
    }
    this.updateChangeSubscription();
  }

  columns$ = new BehaviorSubject<Column[]>([]);
  groupingColumns$ = this.columns$
    .asObservable()
    .pipe(map(columns => columns.filter(column => column.grouping)));

  groupBy: QtGroupBy<T> | null = null;

  /**
   * Determine whether row should be filtered for given query.
   * Override this method to use custom predicate.
   */
  searchPredicate: SearchPredicate = defaultSearchPredicate;

  valueAccessorFn: (row: T, path: string) => unknown = (
    row: T,
    path: string
  ) => {
    return valueAccessor<T>(row, path);
  };

  /**
   * Hold latest data to display.
   *
   * If you want to dynamically set observable as data source,
   * you need to cast it to `any`.
   *
   * ```typescript
   * const dataSouce = new QtDataSource(initialData);
   *
   * setTimeout(() => {
   *   dataSource.data = of([]) as any;
   * }, 2000);
   * ```
   */
  get data(): T[] {
    return this._data.value;
  }
  set data(value: T[] | Observable<T[]>) {
    this.setData(value);
  }

  _data = new BehaviorSubject<T[]>([]);
  // groups$ = new BehaviorSubject<Set<string>>(new Set());
  groups = signal<Set<string>>(new Set());

  /** Stream emitting length of filtered data. */
  readonly length = new BehaviorSubject<number | null>(null);

  /** Stream emitting render data to the table. */
  private readonly renderData = new BehaviorSubject<T[]>([]);

  private renderChangesSubscription: Subscription;

  /** Hold subscription to data observable. */
  private dataSubscription: Subscription | null = null;

  groupedById = signal<string | undefined>(undefined);

  //TODO think about QtRouterSerializer should or not be still used - currently commented out
  constructor(
    initialData: T[] | Observable<T[]>,
    public serializer?: QtRouterSerializer
  ) {
    super();

    this.setData(initialData);
    this.updateChangeSubscription();
  }

  /**
   * Returns observable with latest data after all
   * transformations for connected controls.
   */
  connect(): Observable<T[]> {
    return this.renderData.asObservable();
  }

  /**
   * Unsubscribe from streams and release memory.
   */
  disconnect() {
    this.renderChangesSubscription.unsubscribe();

    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }

    if (this.serializer) {
      this.serializer.disconnect();
    }

    if (this.metaControls) {
      this.metaControls.disconnect();
    }
  }

  updateChangeSubscription() {
    if (this.renderChangesSubscription) {
      this.renderChangesSubscription.unsubscribe();
    }

    if (this.metaControls) {
      this.metaControls.setControls({
        ...(this.groupByComponent && { groupBy: this.groupByComponent }),
        ...(this.sort && { sort: this.sort }),
        ...(this.viewControl && { viewControl: this.viewControl }),
        ...(this.paginator && { paginator: this.paginator }),
        ...(this.filterComp && { filterComponent: this.filterComp }),
        ...(this.historyControl && { historyControl: this.historyControl }),
      });
    }

    this.renderChangesSubscription = this._data
      .pipe(
        search(this.search, this.searchPredicate, this.valueAccessorFn),
        filter(this.filter, this.valueAccessorFn),
        sort(this.sort, this.valueAccessorFn),
        tap(data => {
          this.length.next(data.length);
        }),
        paginate(this.paginator, true),
        switchMap(data => {
          return iif(
            () => !this.groupBy && !this.groupByComponent,
            of(data),
            of(data).pipe(
              mergeMap(() =>
                iif(
                  () => !!this.groupByComponent,
                  of(data).pipe(
                    qtGroupByMeta(
                      this.groupByComponent,
                      this.valueAccessorFn as any,
                      this.groupingColumns$
                    )
                  ),
                  of(data).pipe(
                    qGroupBy(
                      this.groupBy,
                      this.valueAccessorFn as any,
                      this.groupingColumns$
                    )
                  )
                )
              )
            )
          );
        })
      )
      .subscribe(data => {
        const groupedById = this.setGroups(data);
        this.groupedById.set(groupedById);
        this.renderData.next(data);
      });
  }

  /**
   * Set controls, register them in serializer if it exists, and update stream.
   *
   * @param controls Map of controls to set.
   */
  setControls(controls: {
    paginator?: MatPaginator;
    sort?: MatSort;
    search?: QtSearchDirective;
    filter?: QtFilterDirective;
    filterComponent?: QtFilterComponent;
    groupBy?: QtGroupBy<T>;
    metaControls?: MetaControls;
    viewControl?: ViewControl;
    groupByComponent?: PsGroupByComponent;
    historyControl?: MetadataHistoryComponent;
  }) {
    (Object.keys(controls) as Array<keyof typeof controls>).forEach(key => {
      const value = controls[key];

      if (key in this) {
        (this[key] as typeof value) = value;
      }
    });

    if (this.serializer) {
      this.serializer.setControls(controls, true);
    }

    this.updateChangeSubscription();
  }

  setValueAccessor(valueAccessor: (row: T, path: string) => unknown) {
    this.valueAccessorFn = valueAccessor;
  }

  /**
   * Pass data from observable or array to data subject.
   * Unsubscribes from previous data source.
   *
   * @param data Data to be displayed.
   */
  private setData(data: T[] | Observable<T[]>) {
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
      this.dataSubscription = null;
    }

    if (isObservable(data)) {
      this.dataSubscription = data.subscribe(d => this._data.next(d));
    } else {
      this._data.next(data);
    }
  }

  private setGroups(data: T[]): string | null {
    const groupByValues = data
      .filter((item: T): item is T & ExtendDataTable =>
        isExtendedDataTable(item)
      )
      .map(item => item.__groupBy);
    this.groups.set(new Set(groupByValues));

    if (groupByValues.length === 0) {
      return null;
    }

    return (
      data.find(
        (item: any): item is T & ExtendDataTable =>
          typeof item.__groupById === 'string'
      )?.__groupById ?? null
    );
  }
}
